import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../api';
import { Button } from '../components/ui/button';
import { Users, Building2, Bell, RefreshCw, BarChart3, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const AdminDashboard = () => {
  const { isAdmin, getAuthHeader, loading } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [properties, setProperties] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [scraping, setScraping] = useState(false);
  const [activeTab, setActiveTab] = useState('stats');

  useEffect(() => {
    if (isAdmin) {
      loadAllData();
    }
  }, [isAdmin]);

  const loadAllData = async () => {
    setLoadingData(true);
    try {
      const [statsRes, usersRes, propertiesRes, subscriptionsRes] = await Promise.all([
        api.getAdminStatistics(getAuthHeader()),
        api.getAllUsers(getAuthHeader()),
        api.getAllPropertiesAdmin({ limit: 50 }, getAuthHeader()),
        api.getAllSubscriptions(getAuthHeader())
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setProperties(propertiesRes.data);
      setSubscriptions(subscriptionsRes.data);
    } catch (error) {
      console.error('Error loading admin data:', error);
      toast.error('Failed to load admin data');
    } finally {
      setLoadingData(false);
    }
  };

  const handleManualScrape = async () => {
    setScraping(true);
    try {
      const response = await api.triggerManualScrape(getAuthHeader());
      toast.success(`Scraping completed! ${response.data.stats.total_scraped} properties scraped.`);
      loadAllData();
    } catch (error) {
      toast.error('Failed to trigger scraping');
    } finally {
      setScraping(false);
    }
  };

  const handleDeleteProperty = async (propertyId) => {
    if (!window.confirm('Are you sure you want to delete this property?')) {
      return;
    }
    try {
      await api.deleteProperty(propertyId, getAuthHeader());
      toast.success('Property deleted');
      loadAllData();
    } catch (error) {
      toast.error('Failed to delete property');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen py-8" data-testid="admin-dashboard">
      <div className="container mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight font-heading">
            Admin Dashboard
          </h1>
          <Button 
            onClick={handleManualScrape} 
            disabled={scraping}
            data-testid="manual-scrape-btn"
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${scraping ? 'animate-spin' : ''}`} />
            {scraping ? 'Scraping...' : 'Manual Scrape'}
          </Button>
        </div>

        {loadingData ? (
          <div className="text-center py-12">
            <p className="text-slate-600">Loading admin data...</p>
          </div>
        ) : (
          <>
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <div className="bg-white border border-slate-200 rounded-sm p-6" data-testid="stat-users">
                <div className="flex items-center gap-3 mb-2">
                  <Users className="h-5 w-5 text-[#F5A623]" />
                  <h3 className="font-semibold">Total Users</h3>
                </div>
                <p className="text-3xl font-bold">{stats?.total_users || 0}</p>
              </div>

              <div className="bg-white border border-slate-200 rounded-sm p-6" data-testid="stat-properties">
                <div className="flex items-center gap-3 mb-2">
                  <Building2 className="h-5 w-5 text-[#F5A623]" />
                  <h3 className="font-semibold">Total Properties</h3>
                </div>
                <p className="text-3xl font-bold">{stats?.total_properties || 0}</p>
              </div>

              <div className="bg-white border border-slate-200 rounded-sm p-6" data-testid="stat-subscriptions">
                <div className="flex items-center gap-3 mb-2">
                  <Bell className="h-5 w-5 text-[#F5A623]" />
                  <h3 className="font-semibold">Active Alerts</h3>
                </div>
                <p className="text-3xl font-bold">{stats?.total_subscriptions || 0}</p>
              </div>

              <div className="bg-white border border-slate-200 rounded-sm p-6" data-testid="stat-categories">
                <div className="flex items-center gap-3 mb-2">
                  <BarChart3 className="h-5 w-5 text-[#F5A623]" />
                  <h3 className="font-semibold">Categories</h3>
                </div>
                <div className="text-sm space-y-1">
                  {stats?.properties_by_category && Object.entries(stats.properties_by_category).map(([cat, count]) => (
                    <p key={cat}><span className="font-medium">{cat}:</span> {count}</p>
                  ))}
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="border-b border-slate-200 mb-6">
              <nav className="flex gap-6">
                {[
                  { id: 'stats', label: 'Statistics', icon: BarChart3 },
                  { id: 'users', label: 'Users', icon: Users },
                  { id: 'properties', label: 'Properties', icon: Building2 },
                  { id: 'subscriptions', label: 'Subscriptions', icon: Bell },
                ].map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex items-center gap-2 pb-3 border-b-2 transition-colors ${
                        activeTab === tab.id
                          ? 'border-[#F5A623] text-[#F5A623]'
                          : 'border-transparent text-slate-600 hover:text-slate-900'
                      }`}
                      data-testid={`admin-tab-${tab.id}`}
                    >
                      <Icon className="h-4 w-4" />
                      <span className="font-medium">{tab.label}</span>
                    </button>
                  );
                })}
              </nav>
            </div>

            {/* Content */}
            {activeTab === 'stats' && (
              <div className="bg-white border border-slate-200 rounded-sm p-6">
                <h2 className="text-2xl font-semibold mb-4 font-heading">Recent Scrapes</h2>
                <div className="space-y-2">
                  {stats?.recent_scrapes?.slice(0, 10).map((scrape, idx) => (
                    <div key={idx} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                      <span className="font-medium">{scrape.name}</span>
                      <span className="text-sm text-slate-600">{scrape.last_scraped ? formatDate(scrape.last_scraped) : 'Never'}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'users' && (
              <div className="bg-white border border-slate-200 rounded-sm">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="px-6 py-3 text-left text-sm font-medium">Email</th>
                        <th className="px-6 py-3 text-left text-sm font-medium">Role</th>
                        <th className="px-6 py-3 text-left text-sm font-medium">Created</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((user, idx) => (
                        <tr key={user.user_id} className="border-b border-slate-100 last:border-0" data-testid={`user-row-${idx}`}>
                          <td className="px-6 py-3 text-sm">{user.email}</td>
                          <td className="px-6 py-3 text-sm">
                            <span className={`px-2 py-1 rounded text-xs ${user.role === 'admin' ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-800'}`}>
                              {user.role}
                            </span>
                          </td>
                          <td className="px-6 py-3 text-sm text-slate-600">{formatDate(user.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeTab === 'properties' && (
              <div className="bg-white border border-slate-200 rounded-sm">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="px-6 py-3 text-left text-sm font-medium">Title</th>
                        <th className="px-6 py-3 text-left text-sm font-medium">Category</th>
                        <th className="px-6 py-3 text-left text-sm font-medium">Price</th>
                        <th className="px-6 py-3 text-left text-sm font-medium">Location</th>
                        <th className="px-6 py-3 text-left text-sm font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {properties.map((property, idx) => (
                        <tr key={property.property_id} className="border-b border-slate-100 last:border-0" data-testid={`property-row-${idx}`}>
                          <td className="px-6 py-3 text-sm">{property.title}</td>
                          <td className="px-6 py-3 text-sm">{property.category}</td>
                          <td className="px-6 py-3 text-sm">£{property.price?.toLocaleString()}</td>
                          <td className="px-6 py-3 text-sm">{property.location}</td>
                          <td className="px-6 py-3 text-sm">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteProperty(property.property_id)}
                              data-testid={`delete-property-${idx}`}
                            >
                              <Trash2 className="h-4 w-4 text-red-600" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeTab === 'subscriptions' && (
              <div className="bg-white border border-slate-200 rounded-sm">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="px-6 py-3 text-left text-sm font-medium">Email</th>
                        <th className="px-6 py-3 text-left text-sm font-medium">Status</th>
                        <th className="px-6 py-3 text-left text-sm font-medium">Criteria</th>
                        <th className="px-6 py-3 text-left text-sm font-medium">Created</th>
                      </tr>
                    </thead>
                    <tbody>
                      {subscriptions.map((sub, idx) => (
                        <tr key={sub.subscription_id} className="border-b border-slate-100 last:border-0" data-testid={`subscription-row-${idx}`}>
                          <td className="px-6 py-3 text-sm">{sub.email}</td>
                          <td className="px-6 py-3 text-sm">
                            <span className={`px-2 py-1 rounded text-xs ${sub.active ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-800'}`}>
                              {sub.active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="px-6 py-3 text-sm">
                            {Object.keys(sub.criteria).length > 0 ? JSON.stringify(sub.criteria) : 'All'}
                          </td>
                          <td className="px-6 py-3 text-sm text-slate-600">{formatDate(sub.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;